import { useNavigate } from "react-router-dom";
import { buildAuthUrl } from "@/lib/authRedirect";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { 
  User, 
  LogOut, 
  Trophy, 
  Building2, 
  DollarSign, 
  Calendar,
  Plus,
  Users,
  BarChart3,
  Heart,
  Ticket,
  Sun,
  Moon,
  Search,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useTheme } from "@/hooks/useTheme";
import { ThemedLogo } from "@/components/ThemedLogo";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { useIsArenaOrProfessor } from "@/hooks/useHeaderUserData";

export const Header = () => {
  const navigate = useNavigate();
  const { user, signOut, hasRole } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Cacheado por 5min via hook compartilhado com MobileBottomNav —
  // evita refetch duplicado de arena_usuarios a cada troca de rota.
  const { data: isArenaOrProfessor = false } = useIsArenaOrProfessor();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  // Determine user types
  const isOrganizer = hasRole('organizador');
  const isAthlete = hasRole('atleta');
  
  // Atleta logic: user is athlete if they have athlete role OR if they are logged in but not exclusively organizer/arena
  const showAthleteMenu = isAthlete || (user && !isOrganizer && !isArenaOrProfessor);

  // Soluções Dropdown Component - reorganized to focus on Arena + Tournaments
  const SolucoesMenu = () => {
    const arenaLink = user ? '/minhas-arenas' : '/solucao/arenas';
    const torneiosLink = user ? '/torneios' : '/solucao/torneios';
    const erpLink = user ? '/minhas-arenas?focus=financeiro' : '/solucao/erp-financeiro';
    const atletaLink = user ? '/minhas-inscricoes' : '/solucao/portal-atleta';

    return (
      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger className="text-sm font-medium">
              Soluções
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="w-[min(480px,calc(100vw-2rem))] p-4">
                <div className="grid grid-cols-2 gap-3">
                  {/* Arena - Primary */}
                  <NavigationMenuLink asChild>
                    <a
                      className="block select-none rounded-lg p-4 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground cursor-pointer border border-transparent hover:border-primary/20"
                      onClick={() => navigate(arenaLink)}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <div className="text-base font-semibold">Gestão de Arenas</div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Quadras, reservas, mensalistas e aulas em um só lugar
                      </p>
                    </a>
                  </NavigationMenuLink>

                  {/* Torneios - Primary */}
                  <NavigationMenuLink asChild>
                    <a
                      className="block select-none rounded-lg p-4 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground cursor-pointer border border-transparent hover:border-primary/20"
                      onClick={() => navigate(torneiosLink)}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Trophy className="h-5 w-5 text-primary" />
                        </div>
                        <div className="text-base font-semibold">Gestão de Torneios</div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Inscrições, chaveamento e resultados em tempo real
                      </p>
                    </a>
                  </NavigationMenuLink>

                  {/* ERP Financeiro */}
                  <NavigationMenuLink asChild>
                    <a
                      className="block select-none rounded-lg p-4 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground cursor-pointer"
                      onClick={() => navigate(erpLink)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <div className="text-sm font-medium">ERP Financeiro</div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Fluxo de caixa, relatórios e previsões com IA
                      </p>
                    </a>
                  </NavigationMenuLink>

                  {/* Portal do Atleta */}
                  <NavigationMenuLink asChild>
                    <a
                      className="block select-none rounded-lg p-4 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground cursor-pointer"
                      onClick={() => navigate(atletaLink)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <div className="text-sm font-medium">Portal do Atleta</div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Inscrições, histórico e busca de duplas
                      </p>
                    </a>
                  </NavigationMenuLink>
                </div>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>
    );
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4">
        <div className="flex h-12 lg:h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <div className="cursor-pointer touch-manipulation" onClick={() => navigate("/")}>
              <ThemedLogo className="h-auto" width={155} height={40} />
            </div>

            {/* Navigation Links - Desktop Only */}
            <nav className="hidden lg:flex items-center gap-6">
              {/* ========== DESCONECTADO ========== */}
              {!user && (
                <>
                  <SolucoesMenu />
                  <NavLink 
                    to="/torneios-publicos" 
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    activeClassName="text-foreground"
                  >
                    Explorar Torneios
                  </NavLink>
                </>
              )}

              {/* ========== CONECTADO ========== */}
              {user && (
                <>
                  {/* Painel - Sempre para usuários logados */}
                  <NavLink 
                    to="/painel" 
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    activeClassName="text-foreground"
                  >
                    Painel
                  </NavLink>

                  {/* ===== Grupo: Minhas Arenas ===== */}
                  {isArenaOrProfessor && (
                    <NavigationMenu>
                      <NavigationMenuList>
                        <NavigationMenuItem>
                          <NavigationMenuTrigger className="text-sm font-medium">
                            <Building2 className="mr-1.5 h-4 w-4 text-primary" />
                            Minhas Arenas
                          </NavigationMenuTrigger>
                          <NavigationMenuContent>
                            <div className="w-[min(280px,calc(100vw-2rem))] p-2">
                              <NavigationMenuLink asChild>
                                <a className="flex items-start gap-3 rounded-md p-3 hover:bg-accent cursor-pointer" onClick={() => navigate("/minhas-arenas")}>
                                  <Building2 className="h-4 w-4 text-primary mt-0.5" />
                                  <div>
                                    <div className="text-sm font-medium">Gerenciar Arenas</div>
                                    <p className="text-xs text-muted-foreground">Quadras, agenda, mensalistas e financeiro</p>
                                  </div>
                                </a>
                              </NavigationMenuLink>
                            </div>
                          </NavigationMenuContent>
                        </NavigationMenuItem>
                      </NavigationMenuList>
                    </NavigationMenu>
                  )}

                  {/* ===== Grupo: Meus Torneios ===== */}
                  {isOrganizer && (
                    <NavigationMenu>
                      <NavigationMenuList>
                        <NavigationMenuItem>
                          <NavigationMenuTrigger className="text-sm font-medium">
                            <Trophy className="mr-1.5 h-4 w-4 text-primary" />
                            Meus Torneios
                          </NavigationMenuTrigger>
                          <NavigationMenuContent>
                            <div className="w-[min(280px,calc(100vw-2rem))] p-2">
                              <NavigationMenuLink asChild>
                                <a className="flex items-start gap-3 rounded-md p-3 hover:bg-accent cursor-pointer" onClick={() => navigate("/painel-organizador")}>
                                  <BarChart3 className="h-4 w-4 text-primary mt-0.5" />
                                  <div>
                                    <div className="text-sm font-medium">Painel do Organizador</div>
                                    <p className="text-xs text-muted-foreground">Métricas e visão geral</p>
                                  </div>
                                </a>
                              </NavigationMenuLink>
                              <NavigationMenuLink asChild>
                                <a className="flex items-start gap-3 rounded-md p-3 hover:bg-accent cursor-pointer" onClick={() => navigate("/torneios")}>
                                  <Trophy className="h-4 w-4 text-primary mt-0.5" />
                                  <div>
                                    <div className="text-sm font-medium">Gerenciar Torneios</div>
                                    <p className="text-xs text-muted-foreground">Inscrições, chaves e resultados</p>
                                  </div>
                                </a>
                              </NavigationMenuLink>
                              <NavigationMenuLink asChild>
                                <a className="flex items-start gap-3 rounded-md p-3 hover:bg-accent cursor-pointer" onClick={() => navigate("/torneios/novo")}>
                                  <Plus className="h-4 w-4 text-primary mt-0.5" />
                                  <div>
                                    <div className="text-sm font-medium">Criar Torneio</div>
                                    <p className="text-xs text-muted-foreground">Novo torneio em minutos</p>
                                  </div>
                                </a>
                              </NavigationMenuLink>
                            </div>
                          </NavigationMenuContent>
                        </NavigationMenuItem>
                      </NavigationMenuList>
                    </NavigationMenu>
                  )}

                  {/* ===== Grupo: Atleta ===== */}
                  {(showAthleteMenu || isAthlete) && (
                    <NavigationMenu>
                      <NavigationMenuList>
                        <NavigationMenuItem>
                          <NavigationMenuTrigger className="text-sm font-medium">
                            <Users className="mr-1.5 h-4 w-4 text-primary" />
                            Atleta
                          </NavigationMenuTrigger>
                          <NavigationMenuContent>
                            <div className="w-[min(280px,calc(100vw-2rem))] p-2">
                              <NavigationMenuLink asChild>
                                <a className="flex items-start gap-3 rounded-md p-3 hover:bg-accent cursor-pointer" onClick={() => navigate("/atleta")}>
                                  <Users className="h-4 w-4 text-primary mt-0.5" />
                                  <div>
                                    <div className="text-sm font-medium">Portal do Atleta</div>
                                    <p className="text-xs text-muted-foreground">Perfil, jogos, times e estatísticas</p>
                                  </div>
                                </a>
                              </NavigationMenuLink>
                              <NavigationMenuLink asChild>
                                <a className="flex items-start gap-3 rounded-md p-3 hover:bg-accent cursor-pointer" onClick={() => navigate("/minhas-inscricoes")}>
                                  <Ticket className="h-4 w-4 text-primary mt-0.5" />
                                  <div>
                                    <div className="text-sm font-medium">Minhas Inscrições</div>
                                    <p className="text-xs text-muted-foreground">Pagamentos e status das inscrições</p>
                                  </div>
                                </a>
                              </NavigationMenuLink>
                              <NavigationMenuLink asChild>
                                <a className="flex items-start gap-3 rounded-md p-3 hover:bg-accent cursor-pointer" onClick={() => navigate("/favoritos")}>
                                  <Heart className="h-4 w-4 text-primary mt-0.5" />
                                  <div>
                                    <div className="text-sm font-medium">Favoritos</div>
                                    <p className="text-xs text-muted-foreground">Torneios que você marcou para acompanhar</p>
                                  </div>
                                </a>
                              </NavigationMenuLink>
                            </div>
                          </NavigationMenuContent>
                        </NavigationMenuItem>
                      </NavigationMenuList>
                    </NavigationMenu>
                  )}

                  {/* Explorar Torneios - Sempre */}
                  <NavLink 
                    to="/torneios-publicos" 
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    activeClassName="text-foreground"
                  >
                    Explorar
                  </NavLink>

                  {/* Soluções */}
                  <SolucoesMenu />
                </>
              )}
            </nav>
          </div>

          {/* Ações do Usuário - Desktop Only */}
          <div className="hidden lg:flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full h-10 w-10"
              onClick={() => navigate("/torneios-publicos")}
              aria-label="Buscar torneio"
              title="Buscar torneio"
            >
              <Search className="h-5 w-5" />
            </Button>
            {user && <NotificationBell />}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full h-10 w-10 touch-manipulation">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-background z-[100]">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">Minha Conta</p>
                    <p className="text-xs text-muted-foreground truncate mt-1">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/perfil")} className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    <span>Meu Perfil</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer">
                    {theme === "dark" ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                    <span>{theme === "dark" ? "Tema claro" : "Tema escuro"}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sair</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                onClick={() => navigate(buildAuthUrl())}
                className="h-10 px-4 touch-manipulation"
              >
                Entrar
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
